import _ from 'lodash'

import {
  transformDeliveryContact,
  transformReserveOrder,
  transformOrderTemplate,
  transformDepartment,
  transformInventoryThreshold,
  transformInventoryLog,
  transformApiFormErrors,
  addressToString,
  getRecurrenceTimeFrameLabel,
} from '@/lib/utils/transformEntries'

import { useContentful } from '@/components/common/contentful/composables/useContentful'
import { useContentfulComponent } from '@/components/common/contentful/composables/useContentfulComponent'
import { transformExperts } from '@/components/common/contentful/helpers/contentfulHelpers'

import { useFormat } from '@/composables/useFormat'
import { useI18n } from '@/composables/useI18n'
import { useCartStore } from '@/stores/cart'
import { useSessionStore } from '@/stores/session'
import {
  AlgoliaResource,
  AlgoliaProduct,
  AlgoliaSkuProduct,
  AlgoliaInventoryProduct,
  ContentfulProduct,
  ContentfulPage,
  ContentfulResource,
  AlgoliaPage,
  ContentfulProductFields,
  ContentfulPageFields,
  ContentfulResourceFields,
  CartItem,
  ApiCartItem,
  InventoryProduct,
} from '@/types'

type TransformableEntries =
  | AlgoliaResource
  | AlgoliaProduct
  | AlgoliaPage
  | AlgoliaSkuProduct
  | ContentfulProduct
  | ContentfulPage
  | ContentfulResource

export function useTransformEntries() {
  const { t } = useI18n()
  const contentful = useContentful()
  const sessionStore = useSessionStore()
  const { getContentTypeId, getImageUrl, getImageAlt, getSlugsForEducationalResource } =
    useContentfulComponent()
  const cartStore = useCartStore()
  const { formatDate } = useFormat()

  function transformEntry(entry: TransformableEntries) {
    if (!entry) return
    const isContentfulEntry = 'fields' in entry && 'sys' in entry
    try {
      if (isContentfulEntry) {
        const contentType = getContentTypeId<typeof entry>(entry)
        if (contentType === 'product') {
          const item = entry.fields as ContentfulProductFields
          return {
            entryType: 'product',
            slug: item.slug,
            link: `/${item.slug}`,
            img: item.images
              ? getImageUrl(item.images[0], 60, 408, 400)
              : contentful.service.getProductDefaultImage(),
            imgAlt: item.images ? getImageAlt(item.images[0], item.title) : item.title,
            thumbnailImg: item.thumbnailImage ? getImageUrl(item.thumbnailImage) : null,
            title: item.title.trim(),
            subtitle: null,
            description: item.description,
            paragraph: item.descriptionRich,
            date: null,
            additionalDate: null,
            payloadUrl: null,
            parent: {
              type: item.types && item.types.length ? item.types[0].fields?.pageType : null,
              title: item.types && item.types.length ? item.types[0].fields?.title : null,
              slug: item.types && item.types.length ? item.types[0].fields?.slug : null,
              link: item.types && item.types.length ? `/${item.types[0].fields?.slug}` : null,
            },
            isFeatured: item.isFeatured,
            featuredRank: item.featuredRank,
            createdAt: entry.sys.createdAt,
          }
        } else if (contentType === 'page') {
          const item = entry.fields as ContentfulPageFields
          return {
            entryType: 'page',
            slug: item.slug,
            link: contentful.service.getPageBySlug(item.slug)?.link || `/${item.slug}`,
            img: item.image
              ? `${item.image}?fl=progressive&q=70&w=340&h=340`
              : contentful.service.getProductDefaultImage(),
            imgAlt: item.title,
            title: item.title.trim(),
            subtitle: item.subtitle,
            pageType: item.pageType,
            paragraph: null,
            date: null,
            additionalDate: null,
            payloadUrl: null,
            createdAt: entry.sys.createdAt,
          }
        } else if (contentType === 'resource') {
          const item = entry.fields as ContentfulResourceFields
          const defaultImage = contentful.service.getResourceDefaultImage(
            item.resourceGroup.fields.slug,
          )
          let { slug, wrappedSlug, parentSlug } = getSlugsForEducationalResource(
            item.resourceGroup.fields,
            item.title,
          )
          const isBlog = item?.resourceGroup?.fields?.slug === 'blogs'
          let loggedInUrl = item.asset?.fields?.file?.url || item.asset || item.link
          let guestUrl = item.link
          if (isBlog) {
            slug = !item.link ? `blogs/${item.slug}` : item.link
            wrappedSlug = slug
            loggedInUrl = guestUrl = contentful.service.transformSlugToLink(slug)
            parentSlug = contentful.service.getBlogsPage()?.link || ''
          }
          return {
            entryType: 'resource',
            resourceGroup: item.resourceGroup?.fields?.title,
            slug: wrappedSlug,
            link: `/${wrappedSlug}`,
            parent: {
              type: item.resourceGroup?.fields?.pageType,
              title: item.resourceGroup?.fields?.title,
              slug: parentSlug,
              link: `/${parentSlug}`,
            },
            isSelfOpenTarget: isBlog,
            loggedInUrl,
            guestUrl,
            isLoginGated: item.isLoginGated,
            img: getImageUrl(item.image, 80) || defaultImage,
            imgAlt: getImageAlt(item.image, item.title),
            title: item.title.trim(),
            subtitle: item.subtitle,
            description: item.description,
            paragraph: item.paragraphRich,
            date: item.date || null,
            additionalDate: item.additionalDate || null,
            includes: item.includes || [],
            isFeatured: item.isFeatured,
            featuredRank: item.featuredRank,
            createdAt: entry.sys.createdAt,
          }
        }
      } else {
        const contentType = entry.contentType
        if (contentType === 'skuProduct') {
          const item = entry as AlgoliaSkuProduct
          return {
            ...item,
            objectID: item.objectID,
            entryType: 'skuProduct',
            slug: `${item.pgpSlug}/${item.code}`,
            link: `/${item.pgpSlug}/${item.code}`,
            img: contentful.service.getProductDefaultImage(),
            title: item.name.trim(),
            parent: {
              type: null,
              title: item.name.trim(),
              slug: item.pgpSlug,
              link: `/${item.pgpSlug}`,
            },
          }
        } else if (contentType === 'product') {
          const item = entry as AlgoliaProduct
          return {
            objectID: item.objectID,
            entryType: 'product',
            slug: item.slug,
            link: `/${item.slug}`,
            img: item.image
              ? `${item.image}?fl=progressive&q=70&w=340&h=340`
              : contentful.service.getProductDefaultImage(),
            imgAlt: item.imageAlt || item.title,
            title: item.title.trim(),
            subtitle: null,
            description: _.unescape(item.description),
            paragraph: null,
            date: null,
            additionalDate: null,
            payloadUrl: null,
            parent: {
              type: null,
              title: item.parentPage?.title,
              slug: item.parentPage?.slug,
              link: `/${item.parentPage?.slug}`,
            },
            includes: [],
            isFeatured: item.isFeatured,
            featuredRank: item.featuredRank,
            createdAt: item.createdAt,
            isInventory: item.isInventory,
            relatedProducts: item.relatedProducts?.length
              ? item.relatedProducts.map((productEntry) => ({
                  title: productEntry.title,
                  link: `/${productEntry.slug}`,
                }))
              : null,
            metaDescription: item.metaDescription,
          }
        } else if (contentType === 'page') {
          const item = entry as AlgoliaPage
          return {
            objectID: item.objectID,
            entryType: 'page',
            slug: item.slug,
            link: contentful.service.getPageBySlug(item.slug)?.link || `/${item.slug}`,
            img: item.image
              ? `${item.image}?fl=progressive&q=70&w=340&h=340`
              : contentful.service.getProductDefaultImage(),
            imgAlt: item.imageAlt || item.title,
            title: item.title.trim(),
            subtitle: item.description,
            pageType: item.pageType,
            paragraph: null,
            date: null,
            additionalDate: null,
            payloadUrl: null,
            metaDescription: item.metaDescription,
          }
        } else if (contentType === 'resource') {
          const item = entry as AlgoliaResource
          const defaultImage = contentful.service.getResourceDefaultImage(item.parentPage?.slug)
          let { slug, wrappedSlug, parentSlug } = getSlugsForEducationalResource(
            item.parentPage,
            item.title,
            item.objectID,
          )
          let loggedInUrl = item.asset || item.link
          let guestUrl = item.link
          const isBlog = item.resourceGroup === 'Blogs'
          if (isBlog) {
            slug = !item.link ? `blogs/${item.slug}` : item.link
            wrappedSlug = slug
            loggedInUrl = guestUrl = contentful.service.transformSlugToLink(slug)
            parentSlug = contentful.service.getBlogsPage()?.link || ''
          }
          return {
            objectID: item.objectID,
            entryType: 'resource',
            resourceGroup: item.resourceGroup,
            slug: wrappedSlug,
            link: `/${wrappedSlug}`,
            parent: {
              type: null,
              title: item.parentPage?.title,
              slug: parentSlug,
              link: `/${parentSlug}`,
            },
            isSelfOpenTarget: isBlog,
            loggedInUrl,
            guestUrl,
            isLoginGated: item.isLoginGated,
            img: item.image ? `${item.image}?fl=progressive&q=70` : defaultImage,
            imgAlt: item.imageAlt || item.title,
            title: item.title.trim(),
            subtitle: null,
            description: _.unescape(item.description),
            paragraph: null,
            date: item.date || null,
            additionalDate: item.additionalDate,
            includes: [],
            isFeatured: item.isFeatured,
            featuredRank: item.featuredRank,
            createdAt: item.createdAt,
            author: item.author,
            metaDescription: item.metaDescription,
          }
        }
      }
    } catch (e) {
      console.log(e)
    }
  }

  function transformEntries(entries: TransformableEntries[]) {
    return entries?.length ? entries.map(transformEntry).filter((el) => el) : []
  }

  function getInventorySizeField(entry: AlgoliaInventoryProduct) {
    const checkIfStartsWithNumeric = (value: string | null) => !isNaN(Number(value))
    const possibleValuesByPriority = [entry.yield, entry.measurement, entry.ffpeSize]
    const validValues = possibleValuesByPriority.filter((el) => checkIfStartsWithNumeric(el))
    const value = validValues[0] || ''
    const rollQuantityUnit = entry.rollQuantityUnit || ''
    const productTypesBlacklist = ['Biofluids (Blood-derived)', 'Biofluids (Other)']
    const isMeasurementFieldAllowed =
      rollQuantityUnit.toLowerCase() !== 'ml' && !productTypesBlacklist.includes(entry.productType)
    return isMeasurementFieldAllowed ? value : ''
  }

  function transformInventoryEntries({
    entries,
    isRelatedSamples = false,
    facetMap = {},
  }: {
    entries: AlgoliaInventoryProduct[]
    isRelatedSamples?: boolean
    facetMap?: Record<string, Record<string, string | number | boolean | null>>
  }): InventoryProduct[] {
    return entries.map((el) => {
      const generalFields = {
        matrix: el.matrix,
        format: el.format,
        diagnosis: el.diseaseIndication,
        measurement: getInventorySizeField(el),
      }

      let generalData = []

      if (isRelatedSamples) {
        generalData = [
          {
            title: 'Product Code',
            value: el.x3ProductCode,
          },
          {
            title: 'Case ID',
            value: el.bsiIdUi?.toLowerCase().startsWith('adm') ? '' : el.caseIdUi,
          },
          {
            title: el.lotIdUi ? 'Lot ID' : 'Specimen ID',
            value: el.lotIdUi ? el.lotIdUi : el.specimenIdUi,
          },
          {
            title: 'Tumor Grade',
            value: el.tumorGrade,
          },
          {
            title: 'Sub Format',
            value: el.subFormat,
          },
          {
            title: 'STMR',
            value: el.stmr,
          },
          {
            title: 'SNML',
            value: el.snml,
          },
          {
            title: 'SNCR',
            value: el.sncr,
          },
          {
            title: 'SOTR',
            value: el.sotr,
          },
        ].filter((el) => el.value)
      } else {
        generalData = [
          {
            title: 'Product Code',
            value: el.x3ProductCode,
          },
          {
            title: 'Case ID',
            value: el.bsiIdUi?.toLowerCase().startsWith('adm') ? '' : el.caseIdUi,
          },
          {
            title: 'Year of Excision/Collection',
            value: el.yearOfExcision,
          },
          {
            title: el.lotIdUi ? 'Lot ID' : 'Specimen ID',
            value: el.lotIdUi ? el.lotIdUi : el.specimenIdUi,
          },
          {
            title: 'Size/Volume',
            value: getInventorySizeField(el),
          },
          {
            title: 'Recovery Type',
            value: el.recoveryType,
          },
          {
            title: 'Country of Collection',
            value: el.countryOfCollectionSite,
          },
          {
            title: 'Premium Group',
            value: el.premiumGroup ? 'Yes' : 'No',
            info: facetMap.premiumGroup?.info,
          },
        ].filter((el) => el.value)
      }

      const documents = [
        {
          displayTitle: 'IRB Documentation',
          type: 'pdf',
          value: el.irbApprovalLetterLocation,
        },
        {
          displayTitle: 'ICF Documentation',
          type: 'pdf',
          value: el.icfLocation,
        },
        getDocumentImage(el),
      ].filter((el) => el.value)

      const annotationAndClinicalData = [
        {
          title: 'Annotation Related',
          list: el.annotatedResults,
        },
        {
          title: 'Clinical Test',
          list: el.clinicalTests,
        },
      ].filter((el) => el.list && el.list.length)

      annotationAndClinicalData.forEach((item) => {
        if (item && item.list) {
          const uniqueTestTypes = _.uniqBy(item.list, 'testType')
          uniqueTestTypes.forEach((el, i) => {
            if (i % 2 === 0) el.altColor = true
          })

          item.list
            .sort((a, b) => Number(a.id) - Number(b.id))
            .forEach((el) => {
              const foundTestType = uniqueTestTypes.find((type) => type.testType === el.testType)
              if (foundTestType?.altColor) el.altColor = true
              el.assayDate = formatDate(el.assayDate)
            })
        }
      })

      const donorList = [
        {
          title: 'Clinical Diagnosis (Patient)',
          value: el.clinicalDiagnosisPatient,
        },
        {
          title: 'Medical Treatments',
          value: el.medicalTreatments,
        },
        {
          title: 'AJCC/UICC Stage Group',
          value: el.ajccUicc,
        },
        {
          title: 'Medications',
          value: el.medications,
        },
        {
          title: 'Age at Collection',
          value: el.ageOfExcision,
        },
        {
          title: 'Year of Excision/Collection',
          value: el.yearOfExcision,
        },
        {
          title: 'Gender',
          value: el.sex,
        },
        {
          title: 'Race',
          value: el.race,
        },
        {
          title: 'Smoking Status',
          value: el.smokingStatus,
        },
        {
          title: 'Alcohol Status',
          value: el.alcoholStatus,
        },
      ].filter((el) => el.value)

      const specimenList = [
        {
          title: 'Biosample Confirmed Diagnosis',
          value: el.biosampleConfirmedDiagnosis,
        },
        {
          title: 'Format',
          value: el.format,
        },
        {
          title: 'Sub Format',
          value: el.subFormat,
        },
        {
          title: 'Matrix',
          value: el.matrix,
        },
        {
          title: 'FFPE Size',
          value: el.ffpeSize,
        },
        {
          title: 'Tumor Grade',
          value: el.tumorGrade,
        },
        {
          title: 'RIN',
          value: el.rin,
        },
        {
          title: 'STMR',
          value: el.stmr,
        },
        {
          title: 'SNML',
          value: el.snml,
        },
        {
          title: 'SNCR',
          value: el.sncr,
        },
        {
          title: 'SOTR',
          value: el.sotr,
        },
      ].filter((el) => el.value)

      const collectionData = [
        {
          id: 'specimen',
          title: 'Collection Related - Specimen(s)',
          list: specimenList,
        },
        {
          id: 'donor',
          title: 'Collection Related - Donor',
          list: donorList,
        },
      ].filter((el) => el.list.length)

      const cartInventoryProducts = cartStore.inventoryProducts
      const inventoryProductFromCart = cartInventoryProducts.find(
        (inventoryProduct) =>
          inventoryProduct?.inventory?.some(
            (inventoryItem) => inventoryItem.objectID === el.objectID,
          ),
      )

      let rollQuantity = el.rollQuantity || 1
      const isMainPriceParamEditable = rollQuantity >= 2
      if (isMainPriceParamEditable) rollQuantity = Math.floor(rollQuantity)
      const rollQuantityUnit = el.rollQuantityUnit || ''
      const isSalesToStockMainPriceParam = !['ea', 'vil', 'vial', 'vials'].includes(
        rollQuantityUnit.toLowerCase(),
      )

      return {
        code: el.x3ProductCode,
        image: el.image,
        caseId: el.caseId,
        caseIdUi: el.caseIdUi || '',
        specimenIdUi: el.specimenIdUi || '',
        bsiId: el.bsiId || '',
        bsiIdUi: el.bsiIdUi || '',
        lotId: el.lotId || '',
        lotIdUi: el.lotIdUi || '',
        objectID: el.objectID,
        title: el.productDescription,
        generalFields,
        generalData,
        documents,
        annotationAndClinicalData,
        collectionData,
        internalUse: el.internalUse,
        isAddedToCart: !!inventoryProductFromCart,
        price: {
          formattedValue: inventoryProductFromCart?.itemNetPrice?.formattedValue,
          value: inventoryProductFromCart?.itemNetPrice?.value,
        },
        isPriceFinalized: !!inventoryProductFromCart?.itemNetPrice?.value || false,
        uom: el.uom || null,
        leadTime: 0,
        isMainPriceParamEditable,
        isSalesToStockMainPriceParam,
        rollQuantity,
        rollQuantityUnit,
        rollQuantityStep: 1,
        quantityAvailableText: `Quantity Available: ${rollQuantity} ${rollQuantityUnit}`,
      }
    })
  }

  function getDocumentImage(el: AlgoliaInventoryProduct) {
    if (el.wholeSlideImage?.url && el.wholeSlideImage?.title) {
      return {
        displayTitle: el.wholeSlideImage.title,
        value: el.wholeSlideImage.url,
        wholeSlideImage: el.wholeSlideImage.title === 'Whole Slide Image Link',
      }
    } else {
      return { value: null }
    }
  }

  function transformInventoryCartDetails(cartEntry: ApiCartItem) {
    if (!cartEntry.inventory?.length) return []
    return cartEntry.inventory.map(({ objectID, caseIdUi, lotId, lotIdUi, specimenIdUi }) => {
      return {
        objectID,
        list: [
          {
            title: 'Case ID',
            selected: caseIdUi,
          },
          {
            title: lotId ? 'Lot ID' : 'Specimen ID',
            selected: lotIdUi || specimenIdUi,
          },
          {
            title: 'Reserve Order',
            selected: cartEntry.reservedOrderLine,
          },
        ].filter((el) => el.selected),
      }
    })
  }

  function transformCartEntry(entry: CartItem) {
    const inventoryIds = entry.inventory?.length ? entry.inventory.map((el) => el.objectID) : []
    const isInventoryProduct = !!inventoryIds.length
    const inventoryRollUnit = entry.inventory?.[0]?.rollQuantityUnit || ''
    let inventoryRollQuantity = entry.inventory?.[0]?.rollQuantity || 1
    const isMainPriceParamEditable = inventoryRollQuantity >= 2
    if (isMainPriceParamEditable) inventoryRollQuantity = Math.floor(inventoryRollQuantity)
    const isSalesToStockMainPriceParam =
      isInventoryProduct &&
      !['ea', 'vil', 'vial', 'vials'].includes(inventoryRollUnit.toLowerCase())

    const promoPercent = entry.promoPercent || 0
    const totalDiscountPersent = +(promoPercent + entry.customerDiscountPercent).toFixed(2)
    const customerDiscountPercent = entry.customerDiscountPercent
      ? Math.abs(entry.customerDiscountPercent)
      : 0

    return {
      productName: {
        titleTH: t('cart.tablePricedHeaderTitles.productInfo'),
        name: entry.productName,
        url: entry.productUrl,
        code: entry.sku,
      },
      productQty: {
        titleTH: t('cart.tablePricedHeaderTitles.productQty'),
        value: entry.fractionalQty || entry.quantity,
      },
      itemGrossPrice: {
        titleTH: t('cart.tablePricedHeaderTitles.productUnit'),
        formattedValue: entry.itemGrossPrice?.formattedValue,
        value: entry.itemGrossPrice?.value,
      },
      itemNetPrice: {
        titleTH: t('cart.tablePricedHeaderTitles.productUnit'),
        formattedValue: entry.itemNetPrice?.formattedValue,
        value: entry.itemNetPrice?.value,
      },
      totalGrossPrice: {
        titleTH: t('cart.tablePricedHeaderTitles.productTotal'),
        formattedValue: entry.totalGrossPrice?.formattedValue,
        value: entry.totalGrossPrice?.value,
      },
      totalNetPrice: {
        titleTH: t('cart.tablePricedHeaderTitles.productTotal'),
        formattedValue: entry.totalNetPrice?.formattedValue,
        value: entry.totalNetPrice?.value,
      },
      isPromoApplied: entry.isPromoApplied,
      promoAmount: {
        formattedValue: entry.promoAmount?.formattedValue,
        value: entry.promoAmount?.value,
      },
      promoPercent,
      totalDiscountPersent,
      isPositiveTotalDiscountPersent: totalDiscountPersent && Math.sign(totalDiscountPersent) === 1,
      isPositiveCustomerDiscount:
        entry.customerDiscountAmount?.value && Math.sign(entry.customerDiscountAmount.value) === 1,
      customerDiscountAmount: {
        formattedValue: entry.customerDiscountAmount?.formattedValue?.replace('-', ''),
        value: entry.customerDiscountAmount?.value
          ? Math.abs(entry.customerDiscountAmount.value)
          : 0,
      },
      customerDiscountPercent,
      pricingParams: entry.pricingParams,
      img: entry.productImageUrl || contentful.service.getProductDefaultImage(),
      unit: sessionStore.isUserLoggedIn ? entry.unit.name : null,
      customizations: entry.customizations,
      id: entry.pk,
      entryNumber: entry.entryNumber,
      deliveryContact: entry.deliveryContact,
      shippingAddress: entry.shippingAddress,
      disableQty: entry.disableQty,
      inventoryDetails: transformInventoryCartDetails(entry),
      premiumGroups: entry.inventory?.length ? entry.inventory.map((el) => el.premiumGroup) : [],
      inventoryIds,
      isInventoryProduct,
      inventoryRollQuantity,
      inventoryRollUnit,
      isMainPriceParamEditable,
      isSalesToStockMainPriceParam,
      leadTime: entry.leadTime == null ? null : entry.leadTime,
      isCustomSku: entry.isCustomSku,
      reservedOrderLine: entry.reservedOrderLine,
    }
  }

  return {
    transformEntry,
    transformEntries,
    transformInventoryEntries,
    transformInventoryCartDetails,
    transformCartEntry,
    transformDeliveryContact,
    transformReserveOrder,
    transformOrderTemplate,
    transformDepartment,
    transformInventoryThreshold,
    transformInventoryLog,
    transformApiFormErrors,
    addressToString,
    transformExperts,
    getRecurrenceTimeFrameLabel,
  }
}
